/* Lumetier Landing — remaining stateless sections.
   Grouped here because they're all simple presentational chunks that share
   the same set of imports (Reveal, gold tokens, lucide icons). One file
   keeps the per-section files lean while still cleanly extracting them
   from the main Landing.jsx composition file. */

import { Link } from "react-router-dom";
import { Check, Sparkles } from "lucide-react";
import { Reveal } from "./Reveal";
import { GOLD, GOLD_LIGHT, FEATURES, PRICING } from "./constants";

export function StatsStripSection() {
  return (
    <section className="border-y border-white/[0.07] bg-white/[0.02]">
      <div className="max-w-6xl mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
        {[
          { n: "5+", l: "Years shooting" },
          { n: "300+", l: "Sessions delivered" },
          { n: "100%", l: "Built in-house" },
          { n: "1", l: "Tool for everything" },
        ].map((s, i) => (
          <Reveal key={s.l} delay={i * 0.1}>
            <div>
              <div className="text-3xl md:text-4xl font-light"
                   style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: GOLD }}>
                {s.n}
              </div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-stone-500 mt-1.5">{s.l}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function PainPointsSection({ ChapterEyebrow, GoldRule, DisplayHeadline, EditorialNumeral, FilmGrain }) {
  return (
    <section className="relative max-w-[1480px] mx-auto px-6 lg:px-12 py-28 lg:py-36">
      <FilmGrain opacity={0.03} tone="cool" />
      <div className="relative">
        <ChapterEyebrow chapter="❖" label="Why now" />
        <GoldRule width="3rem" className="mt-4 mb-8" />
        <DisplayHeadline size="hero" tone="charcoal" className="max-w-[18ch]" data-reveal="rise">
          Five tools. Two hundred a month. <em>Hours</em> lost on a Tuesday.
        </DisplayHeadline>
        <div className="mt-16 grid md:grid-cols-3">
          {[
            { n: "01", h: "Five-plus tools",     b: "HoneyBook for contracts. Pixieset for galleries. Square for payments. Calendly for booking. Mailchimp for reminders. Sound familiar?" },
            { n: "02", h: "Two hundred a month", b: "Subscriptions add up fast. You're paying for ten things and using two features each. None of them talk." },
            { n: "03", h: "Hours lost",          b: "Every Friday night reconciling who paid, who didn't, what got delivered, what's still owed. There is a better way." },
          ].map((p, i) => (
            <div key={p.n} data-reveal="rise"
                 className={`relative px-6 lg:px-10 py-10 ${i > 0 ? "md:border-l border-[color:var(--gold-foil)]/15" : ""}`}>
              <EditorialNumeral value={p.n} size="lg" style="outline" tone="gold" />
              <h3 className="font-serif text-2xl md:text-3xl mt-6 leading-tight text-[color:var(--cream)]">{p.h}</h3>
              <GoldRule width="2rem" className="mt-5 mb-5" />
              <p className="ed-body-lg text-[color:var(--cream)]/65 leading-relaxed">{p.b}</p>
            </div>
          ))}
        </div>
        <a href="#tour"
           className="absolute right-6 -bottom-6 md:right-0 md:bottom-0 font-serif italic text-[color:var(--gold-base)] hover:text-[color:var(--gold-foil)] transition-colors text-lg md:text-xl">
          a better way →
        </a>
      </div>
    </section>
  );
}

export function FeaturesSection() {
  return (
    <section id="features" className="border-t border-white/[0.07] py-28">
      <div className="max-w-6xl mx-auto px-6">
        <Reveal>
          <div className="text-center mb-16">
            <div className="text-[11px] uppercase tracking-[0.32em] text-stone-500 mb-5">Everything you need</div>
            <h2 className="text-4xl md:text-5xl font-light" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              Six tools,{" "}
              <em className="italic"
                  style={{ backgroundImage: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`,
                           WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                one place.
              </em>
            </h2>
            <div className="h-px w-14 mx-auto mt-7"
                 style={{ background: `linear-gradient(to right, transparent, ${GOLD}, transparent)` }} />
          </div>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <Reveal key={f.name} delay={i * 0.07}>
                <div className="editorial-card-hover bg-white/[0.03] border border-white/[0.08] p-7 h-full hover:bg-white/[0.06] hover:border-white/[0.16] transition-all group cursor-default">
                  <div className="w-11 h-11 flex items-center justify-center mb-6"
                       style={{ background: `${GOLD}18`, color: GOLD }}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-2xl font-light mb-3 text-stone-100"
                      style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{f.name}</h3>
                  <p className="text-sm text-stone-400 leading-relaxed">{f.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function DashboardMockupSection() {
  return (
    <section className="max-w-6xl mx-auto px-6 py-28">
      <Reveal>
        <div className="text-center mb-14">
          <div className="text-[11px] uppercase tracking-[0.32em] text-stone-500 mb-5">A real working product</div>
          <h2 className="text-4xl md:text-5xl font-light" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
            Not a prototype.<br />
            <span className="text-stone-400">A studio that runs on it.</span>
          </h2>
          <p className="text-stone-500 mt-6 max-w-xl mx-auto text-sm leading-relaxed">
            Lumetier is the same engine that runs{" "}
            <a href="https://allure-image.com" target="_blank" rel="noopener noreferrer" className="underline text-stone-300 hover:text-white">Allure Image</a>
            &apos;s full-time photography business. Every feature was battle-tested on real clients before you&apos;ll ever see it.
          </p>
        </div>
      </Reveal>
      <Reveal delay={0.3}>
        <div className="editorial-card-hover mx-auto max-w-4xl border border-white/[0.12] bg-[#0D0F14]">
          <div className="flex items-center gap-1.5 px-4 h-10 border-b border-white/[0.08]">
            <div className="w-2.5 h-2.5 rounded-full bg-stone-700" />
            <div className="w-2.5 h-2.5 rounded-full bg-stone-700" />
            <div className="w-2.5 h-2.5 rounded-full bg-stone-700" />
            <div className="ml-3 text-[11px] text-stone-600 font-mono">studio.lumetiercreatives.com/admin</div>
          </div>
          <div className="flex">
            <div className="w-48 shrink-0 bg-[#080A0E] border-r border-white/[0.07] p-4 hidden md:block">
              <div className="space-y-1">
                {["Overview", "Bookings", "Galleries", "Invoices", "Contracts"].map((l, i) => (
                  <div key={l}
                       className={`px-3 py-2 text-xs rounded-sm ${i === 0 ? "text-stone-100 bg-white/[0.08]" : "text-stone-500"}`}
                       style={i === 0 ? { borderLeft: `2px solid ${GOLD}` } : {}}>{l}</div>
                ))}
              </div>
            </div>
            <div className="flex-1 p-5 grid grid-cols-2 sm:grid-cols-4 gap-3 text-stone-100">
              {[
                { label: "Upcoming sessions", val: "6",       sub: "this week",         color: "border-t-blue-500/50" },
                { label: "Pending bookings",  val: "7",       sub: "awaiting deposit",  color: "border-t-amber-500/50" },
                { label: "Revenue (mo)",      val: "$4,725",  sub: "+18% vs last",      color: "border-t-emerald-500/50" },
                { label: "Open invoices",     val: "1",       sub: "due Friday",        color: "border-t-violet-500/50" },
              ].map((s) => (
                <div key={s.label} className={`bg-white/[0.03] border border-white/[0.08] border-t-2 ${s.color} p-4`}>
                  <div className="text-[9px] uppercase tracking-[0.2em] text-stone-500">{s.label}</div>
                  <div className="text-2xl font-light mt-1 text-stone-100"
                       style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{s.val}</div>
                  <div className="text-[10px] text-stone-600 mt-1">{s.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

export function PricingSection() {
  return (
    <section id="pricing" className="max-w-6xl mx-auto px-6 py-28">
      <Reveal>
        <div className="text-center mb-14">
          <div className="text-[11px] uppercase tracking-[0.32em] text-stone-500 mb-5">Charter pricing</div>
          <h2 className="text-4xl md:text-5xl font-light" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
            Honest pricing.<br />
            <span className="text-stone-400">Locked in for life.</span>
          </h2>
          <p className="text-stone-500 mt-6 max-w-xl mx-auto text-sm leading-relaxed">
            Join the waitlist now and your first 24 months are locked at charter pricing. We won&apos;t raise it on you.
          </p>
        </div>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-4 mt-12">
        {PRICING.map((tier, i) => (
          <Reveal key={tier.name} delay={i * 0.1}>
            <div className="editorial-card-hover flex flex-col h-full p-8"
                 style={tier.highlight
                   ? { background: "linear-gradient(#0D0F14, #0D0F14) padding-box, linear-gradient(135deg, #C9A961, #EDD78C, #C9A961) border-box", border: "1px solid transparent" }
                   : { background: "#0D0F14", border: "1px solid rgba(255,255,255,0.08)" }}>
              {tier.highlight && (
                <div className="text-[10px] uppercase tracking-[0.28em] mb-3" style={{ color: GOLD }}>★ Most popular</div>
              )}
              <h3 className="text-3xl font-light text-stone-100" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{tier.name}</h3>
              <div className="mt-5 mb-5">
                <span className="text-5xl font-light text-stone-100" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>${tier.price}</span>
                <span className="text-stone-500 text-sm">/mo</span>
              </div>
              <p className="text-sm text-stone-500 mb-7 leading-relaxed flex-grow-0">{tier.line}</p>
              <ul className="space-y-3 mb-8 text-sm flex-1">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: GOLD }} />
                    <span className="text-stone-300">{f}</span>
                  </li>
                ))}
              </ul>
              <a href="#waitlist"
                 className={`text-center px-4 h-11 inline-flex items-center justify-center text-xs uppercase tracking-[0.22em] transition-all ${
                   tier.highlight ? "text-stone-900 font-medium hover:opacity-90"
                                  : "border border-white/20 text-stone-300 hover:border-white/50 hover:text-white"}`}
                 style={tier.highlight ? { background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})` } : {}}>
                Get an invite
              </a>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function ApplySection({ name, setName, email, setEmail, photogType, setPhotogType, submitting, submitted, onSubmit }) {
  if (submitted) {
    return (
      <section id="apply" className="border-t border-white/[0.07] py-28">
        <div className="max-w-xl mx-auto px-6">
          <Reveal>
            <div className="text-center bg-white/[0.03] border border-white/[0.1] p-14" data-testid="apply-success">
              <Sparkles className="w-10 h-10 mx-auto mb-6" style={{ color: GOLD }} />
              <h2 className="text-4xl font-light mb-5 text-stone-100" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                Application received.
              </h2>
              <p className="text-stone-400 leading-relaxed mb-6 text-sm">
                I&apos;ll personally email you a private invite link with your onboarding flow — usually within 48 hours. Look out for a note from <span className="text-stone-200">hello@lumetiercreatives.com</span>.
              </p>
              <p className="text-[11px] uppercase tracking-[0.22em] text-stone-600">
                Already have an invite? <Link to="/admin/login" className="underline">Sign in</Link>.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    );
  }
  return (
    <section id="apply" className="border-t border-white/[0.07] py-28">
      <div className="max-w-xl mx-auto px-6">
        <Reveal>
          <div className="text-center mb-12">
            <Sparkles className="w-7 h-7 mx-auto mb-5" style={{ color: GOLD }} />
            <div className="text-[11px] uppercase tracking-[0.32em] text-stone-500 mb-5">Apply for access</div>
            <h2 className="text-4xl md:text-5xl font-light text-stone-100" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>Get an invite.</h2>
            <p className="text-stone-500 mt-6 leading-relaxed text-sm">
              Lumetier is open to a small group of working photographers right now — <span className="text-stone-200">free to use</span> while we&apos;re early. Tell me about your studio and I&apos;ll send you a personal invite link with a one-tap onboarding flow.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.3}>
          <form onSubmit={onSubmit} className="bg-white/[0.03] border border-white/[0.08] p-8 space-y-6" data-testid="apply-form">
            {[
              { label: "Your name", value: name, onChange: setName, type: "text", testid: "wl-name", placeholder: "Jane Smith" },
              { label: "Email", value: email, onChange: setEmail, type: "email", testid: "wl-email", placeholder: "jane@studio.com" },
            ].map(({ label, value, onChange, type, testid, placeholder }) => (
              <label key={label} className="block">
                <span className="text-[10px] uppercase tracking-[0.22em] text-stone-500">{label}</span>
                <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
                       className="mt-2 w-full bg-transparent border-b border-white/[0.15] py-3 outline-none focus:border-[#C9A961] placeholder:text-stone-700 text-stone-100 text-base transition-colors"
                       data-testid={testid} />
              </label>
            ))}
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.22em] text-stone-500">What kind of photographer are you?</span>
              <select value={photogType} onChange={(e) => setPhotogType(e.target.value)}
                      className="mt-2 w-full bg-[#0D0F14] border-b border-white/[0.15] py-3 outline-none focus:border-[#C9A961] text-stone-300 text-base transition-colors"
                      data-testid="wl-type">
                <option value="">Select one…</option>
                <option value="wedding">Wedding</option>
                <option value="family">Family / portrait</option>
                <option value="newborn">Newborn</option>
                <option value="branding">Branding / commercial</option>
                <option value="event">Event</option>
                <option value="other">Other</option>
              </select>
            </label>
            <button type="submit" disabled={submitting} data-testid="wl-submit"
                    className="w-full px-5 h-12 text-xs uppercase tracking-[0.22em] text-stone-900 font-medium hover:opacity-90 disabled:opacity-50 transition-opacity mt-2"
                    style={{ background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})` }}>
              {submitting ? "Sending…" : "Send my application →"}
            </button>
            <p className="text-[11px] text-stone-600 text-center pt-1">
              Already have an invite link? <Link to="/admin/login" className="underline hover:text-stone-300">Sign in</Link> instead.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

export function FooterSection() {
  return (
    <footer className="border-t border-white/[0.07] py-12">
      <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-6">
        <div className="text-lg font-light tracking-[0.42em] text-stone-400"
             style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>L U M E T I E R</div>
        <div className="flex items-center gap-8 text-[11px] uppercase tracking-[0.22em] text-stone-600">
          <a href="https://allure-image.com" target="_blank" rel="noopener noreferrer" className="hover:text-stone-200 transition-colors">Made by Allure Image</a>
          <Link to="/lumetier-preview" className="hover:text-stone-200 transition-colors">Brand</Link>
        </div>
      </div>
    </footer>
  );
}
